import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { db, functions } from '../config/firebase';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import { firebaseErrorKey } from '../lib/firebaseErrors';
import { isPlausibleInviteCode, normalizeInviteCode, INVITE_CODE_MAX_LENGTH } from '../lib/inviteCode';
import { sendCheckIn, type CheckInType } from '../services/checkIns';

interface Contact {
  id: string;
  name: string;
  phoneNumber?: string;
  email?: string;
  username?: string;
  relationship: string;
  linkedUserId?: string;
  hasAppInstalled: boolean;
}

interface HelpRequest {
  id: string;
  type: string;
  description: string;
  priority: string;
  status: string;
  createdAt?: Timestamp;
  createdByName: string;
}

const ROLE_KEYS = {
  care_recipient: 'dashboard.role.careRecipient',
  caregiver: 'dashboard.role.caregiver',
  admin: 'dashboard.role.admin',
} as const;

const ROLE_ICONS = {
  care_recipient: '👴',
  caregiver: '👨‍⚕️',
  admin: '🛡️',
} as const;

/** Statuses that still need someone to act. `open` is the legacy spelling of `pending`. */
const OPEN_STATUSES = ['pending', 'open'];

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const { currentUser, userProfile, profileProblem } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [requests, setRequests] = useState<HelpRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'contacts' | 'requests' | 'profile'>('contacts');
  const [loadError, setLoadError] = useState(false);
  const [sendingCheckin, setSendingCheckin] = useState<CheckInType | null>(null);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [inviteCodeInput, setInviteCodeInput] = useState('');
  const [joiningCircle, setJoiningCircle] = useState(false);

  // Relationship, status and category arrive from Firestore as values written by the
  // mobile apps. Translate the known ones; fall back to the raw value for anything else.
  const enumLabel = (prefix: string, value?: string) =>
    value ? t(`${prefix}.${value.toLowerCase()}`, { defaultValue: value }) : '';

  useEffect(() => {
    if (!currentUser) {
      navigate('/signin');
      return;
    }

    // No profile to show (missing or unreadable) — rendered below, nothing to listen to.
    if (!userProfile) {
      return;
    }

    const businessId = userProfile.businessId;
    if (!businessId) {
      setLoading(false);
      return;
    }

    // Listen to contacts (camelCase schema, unified across iOS/Android)
    const contactsQuery = query(
      collection(db, 'contacts'),
      where('businessId', '==', businessId)
    );

    const unsubContacts = onSnapshot(contactsQuery, (snapshot) => {
      const contactsList: Contact[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        contactsList.push({
          id: doc.id,
          name: data.name || '',
          phoneNumber: data.phoneNumber || data.phone_number,
          email: data.email,
          username: data.username,
          relationship: data.relationship || 'other',
          linkedUserId: data.linkedUserId || data.linked_user_id,
          hasAppInstalled: !!(data.linkedUserId || data.linked_user_id)
        });
      });
      setContacts(contactsList);
      setLoading(false);
    }, (error) => {
      console.error('Error loading contacts:', error);
      setLoadError(true);
      setLoading(false);
    });

    // Listen to help requests
    const requestsQuery = query(
      collection(db, 'requests'),
      where('businessId', '==', businessId)
    );

    const unsubRequests = onSnapshot(requestsQuery, (snapshot) => {
      const requestsList: HelpRequest[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        requestsList.push({
          id: doc.id,
          type: data.category || data.type || 'other',
          description: data.description || '',
          priority: data.priority || 'medium',
          status: data.status || 'pending',
          createdAt: data.createdAt || data.created_at || undefined,
          createdByName: data.createdByName || data.created_by_name || 'Unknown'
        });
      });
      setRequests(requestsList.sort((a, b) =>
        (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0)
      ));
    }, (error) => {
      console.error('Error loading requests:', error);
      setLoadError(true);
    });

    return () => {
      unsubContacts();
      unsubRequests();
    };
  }, [currentUser, userProfile, navigate]);

  // Check-ins belong to the person being cared for (iOS/Android only offer it to
  // them, and the security rules only accept a check-in about yourself).
  const canCheckIn = userProfile?.role === 'care_recipient' && !!userProfile.businessId;

  const handleCheckIn = async (type: CheckInType) => {
    if (!userProfile?.businessId) return;

    setSendingCheckin(type);
    try {
      await sendCheckIn({
        userId: userProfile.id,
        userName: userProfile.displayName,
        businessId: userProfile.businessId,
        type,
        recipientIds: contacts
          .map((c) => c.linkedUserId)
          .filter((id): id is string => !!id),
      });
      alert(t('dashboard.contacts.checkinSuccess'));
    } catch (error) {
      console.error('Error sending check-in:', error);
      alert(t('dashboard.contacts.checkinError'));
    } finally {
      setSendingCheckin(null);
    }
  };

  const inviteCode = normalizeInviteCode(inviteCodeInput);

  const joinCareCircle = async () => {
    if (!isPlausibleInviteCode(inviteCode) || !userProfile) return;

    setJoiningCircle(true);
    try {
      // acceptInvite is an onCall function; it reads the user from the auth context
      const acceptInvite = httpsCallable(functions, 'acceptInvite');
      await acceptInvite({ inviteCode });

      alert(t('dashboard.join.success'));
      setShowJoinDialog(false);
      setInviteCodeInput('');
      window.location.reload();
    } catch (error) {
      console.error('Error joining care circle:', error);
      alert(t('dashboard.join.error', { message: t(firebaseErrorKey(error)) }));
    } finally {
      setJoiningCircle(false);
    }
  };

  if (!userProfile) {
    return (
      <div className="dashboard-problem">
        <h1>{t(profileProblem === 'unavailable' ? 'dashboard.problem.unavailableTitle' : 'dashboard.problem.missingTitle')}</h1>
        <p>{t(profileProblem === 'unavailable' ? 'dashboard.problem.unavailableBody' : 'dashboard.problem.missingBody')}</p>
        <div className="header-actions">
          {profileProblem === 'unavailable' && (
            <button onClick={() => window.location.reload()} className="btn-join">{t('dashboard.problem.retry')}</button>
          )}
        </div>
        <DashboardStyles />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>{t('dashboard.loading')}</p>
        <DashboardStyles />
      </div>
    );
  }

  const roleBadge = `${ROLE_ICONS[userProfile.role]} ${t(ROLE_KEYS[userProfile.role])}`;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>{t('dashboard.welcome', { name: userProfile?.displayName })}</h1>
          <p className="role-badge">{roleBadge}</p>
        </div>
        <div className="header-actions">
          {userProfile?.role === 'caregiver' && !userProfile.businessId && (
            <button onClick={() => setShowJoinDialog(true)} className="btn-join">
              + {t('dashboard.joinCircle')}
            </button>
          )}
        </div>
      </div>

      {loadError && (
        <div className="dashboard-error" role="alert">{t('dashboard.loadError')}</div>
      )}

      <div className="dashboard-tabs">
        <button
          className={`tab ${activeTab === 'contacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contacts')}
        >
          👥 {t('dashboard.tabs.contacts', { count: contacts.length })}
        </button>
        <button
          className={`tab ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          🆘 {t('dashboard.tabs.requests', { count: requests.filter(r => OPEN_STATUSES.includes(r.status.toLowerCase())).length })}
        </button>
        <button
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          👤 {t('dashboard.tabs.profile')}
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'contacts' && (
          <div className="contacts-view">
            <div className="section-header">
              <h2>{t('dashboard.contacts.title')}</h2>
              {canCheckIn && (
                <div className="checkin-actions">
                  {(['morning', 'night'] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => handleCheckIn(type)}
                      disabled={sendingCheckin !== null}
                      className="btn-checkin"
                    >
                      {sendingCheckin === type
                        ? t('dashboard.contacts.sendingCheckin')
                        : `${type === 'morning' ? '☀️' : '🌙'} ${t(`dashboard.contacts.checkin.${type}`)}`}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {contacts.length === 0 ? (
              <div className="empty-state">
                <p>{t('dashboard.contacts.empty')}</p>
              </div>
            ) : (
              <div className="contacts-grid">
                {contacts.map((contact) => (
                  <div key={contact.id} className="contact-card">
                    <div className="contact-avatar">
                      {contact.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="contact-info">
                      <h3>{contact.name}</h3>
                      {contact.username && <p className="username">@{contact.username}</p>}
                      {contact.email && <p className="contact-detail">📧 {contact.email}</p>}
                      {contact.phoneNumber && <p className="contact-detail">📱 {contact.phoneNumber}</p>}
                      <p className="contact-detail">
                        💼 {enumLabel('dashboard.contacts.relationship', contact.relationship)}
                      </p>
                      {contact.hasAppInstalled && (
                        <span className="app-badge">✓ {t('dashboard.contacts.hasApp')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'requests' && (
          <div className="requests-view">
            <h2>{t('dashboard.requests.title')}</h2>

            {requests.length === 0 ? (
              <div className="empty-state">
                <p>{t('dashboard.requests.empty')}</p>
              </div>
            ) : (
              <div className="requests-list">
                {requests.map((request) => (
                  <div key={request.id} className={`request-card status-${request.status}`}>
                    <div className="request-header">
                      <span className="request-type">
                        {enumLabel('dashboard.requests.category', request.type)}
                      </span>
                      <span className={`status-badge ${request.status}`}>
                        {enumLabel('dashboard.requests.status', request.status)}
                      </span>
                    </div>
                    <p className="request-description">{request.description}</p>
                    <div className="request-footer">
                      <span className="request-author">
                        {t('dashboard.requests.by', { name: request.createdByName })}
                      </span>
                      <span className="request-time">
                        {request.createdAt?.toDate?.().toLocaleString(i18n.language)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="profile-view">
            <h2>{t('dashboard.profile.title')}</h2>
            <div className="profile-card">
              <div className="profile-avatar-large">
                {userProfile?.displayName.charAt(0).toUpperCase()}
              </div>
              <div className="profile-details">
                <div className="profile-field">
                  <label>{t('dashboard.profile.name')}</label>
                  <p>{userProfile?.displayName}</p>
                </div>
                <div className="profile-field">
                  <label>{t('dashboard.profile.email')}</label>
                  <p>{userProfile?.email}</p>
                </div>
                {userProfile?.username && (
                  <div className="profile-field">
                    <label>{t('dashboard.profile.username')}</label>
                    <p>@{userProfile.username}</p>
                  </div>
                )}
                {userProfile?.inviteCode && (
                  <div className="profile-field">
                    <label>{t('dashboard.profile.inviteCode')}</label>
                    <div className="invite-code-display">{userProfile.inviteCode}</div>
                    <small>{t('dashboard.profile.inviteCodeHint')}</small>
                  </div>
                )}
                <div className="profile-field">
                  <label>{t('dashboard.profile.role')}</label>
                  <p>{t(ROLE_KEYS[userProfile.role])}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Join Care Circle Dialog */}
      {showJoinDialog && (
        <div className="modal-overlay" onClick={() => setShowJoinDialog(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{t('dashboard.join.title')}</h2>
            <p>{t('dashboard.join.subtitle')}</p>

            <input
              type="text"
              value={inviteCodeInput}
              onChange={(e) => setInviteCodeInput(e.target.value.toUpperCase())}
              placeholder={t('dashboard.join.placeholder')}
              aria-label={t('dashboard.join.inputLabel')}
              maxLength={INVITE_CODE_MAX_LENGTH + 2}
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              className="invite-code-input"
              autoFocus
            />

            <div className="modal-actions">
              <button
                onClick={joinCareCircle}
                disabled={joiningCircle || !isPlausibleInviteCode(inviteCode)}
                className="btn-modal-primary"
              >
                {joiningCircle ? t('dashboard.join.submitting') : t('dashboard.join.submit')}
              </button>
              <button
                onClick={() => setShowJoinDialog(false)}
                className="btn-modal-secondary"
              >
                {t('dashboard.join.cancel')}
              </button>
            </div>

            <div className="qr-hint">
              <p>💡 <strong>{t('dashboard.join.qrHintLabel')}</strong> {t('dashboard.join.qrHint')}</p>
            </div>
          </div>
        </div>
      )}

      <DashboardStyles />
    </div>
  );
}

function DashboardStyles() {
  return (
    <style>{`
        .dashboard {
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 20px;
        }

        .dashboard-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          gap: 20px;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #4285f4;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 2px solid #e0e0e0;
        }

        .dashboard-header h1 {
          font-size: 32px;
          color: #1a1a1a;
          margin-bottom: 8px;
        }

        .role-badge {
          font-size: 16px;
          color: #666;
        }

        .btn-signout {
          padding: 10px 24px;
          background: #f44336;
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-signout:hover {
          background: #d32f2f;
        }

        .dashboard-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 32px;
          border-bottom: 2px solid #e0e0e0;
        }

        .tab {
          padding: 12px 24px;
          background: none;
          border: none;
          border-bottom: 3px solid transparent;
          font-size: 16px;
          font-weight: 600;
          color: #666;
          cursor: pointer;
          transition: all 0.2s;
        }

        .tab:hover {
          color: #4285f4;
        }

        .tab.active {
          color: #4285f4;
          border-bottom-color: #4285f4;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .btn-checkin {
          padding: 10px 20px;
          background: #4caf50;
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-checkin:hover:not(:disabled) {
          background: #45a049;
        }

        .btn-checkin:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .contacts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 20px;
        }

        .contact-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
          transition: box-shadow 0.2s;
        }

        .contact-card:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .contact-avatar {
          width: 60px;
          height: 60px;
          background: #4285f4;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          font-weight: bold;
          flex-shrink: 0;
        }

        .contact-info {
          flex: 1;
        }

        .contact-info h3 {
          font-size: 18px;
          margin-bottom: 4px;
        }

        .username {
          color: #4285f4;
          font-size: 14px;
          margin-bottom: 8px;
        }

        .contact-detail {
          font-size: 14px;
          color: #666;
          margin: 4px 0;
        }

        .app-badge {
          display: inline-block;
          margin-top: 8px;
          padding: 4px 12px;
          background: #4caf50;
          color: white;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .requests-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .request-card {
          padding: 20px;
          background: white;
          border: 2px solid #e0e0e0;
          border-left: 4px solid #4285f4;
          border-radius: 8px;
        }

        .request-card.status-pending {
          border-left-color: #ff9800;
        }

        .request-card.status-in_progress {
          border-left-color: #2196f3;
        }

        .request-card.status-completed {
          border-left-color: #4caf50;
        }

        .request-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .request-type {
          font-weight: 600;
          text-transform: capitalize;
        }

        .status-badge {
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .status-badge.pending {
          background: #fff3e0;
          color: #f57c00;
        }

        .status-badge.in_progress {
          background: #e3f2fd;
          color: #1976d2;
        }

        .status-badge.completed {
          background: #e8f5e9;
          color: #388e3c;
        }

        .request-description {
          color: #333;
          margin-bottom: 12px;
        }

        .request-footer {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          color: #666;
        }

        .profile-card {
          max-width: 600px;
          padding: 32px;
          background: white;
          border: 2px solid #e0e0e0;
          border-radius: 12px;
        }

        .profile-avatar-large {
          width: 120px;
          height: 120px;
          background: #4285f4;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 48px;
          font-weight: bold;
          margin: 0 auto 32px;
        }

        .profile-details {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .profile-field label {
          display: block;
          font-weight: 600;
          color: #666;
          font-size: 13px;
          margin-bottom: 4px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .profile-field p {
          font-size: 16px;
          color: #333;
        }

        .invite-code-display {
          font-size: 32px;
          font-weight: bold;
          color: #4285f4;
          letter-spacing: 4px;
          font-family: monospace;
          margin: 8px 0;
        }

        .profile-field small {
          color: #666;
          font-size: 13px;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #666;
        }

        .header-actions {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .btn-join {
          padding: 10px 20px;
          background: #4caf50;
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-join:hover {
          background: #45a049;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-content {
          background: white;
          padding: 40px;
          border-radius: 16px;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }

        .modal-content h2 {
          margin-bottom: 16px;
          color: #1a1a1a;
        }

        .modal-content p {
          color: #666;
          margin-bottom: 24px;
        }

        .invite-code-input {
          width: 100%;
          padding: 16px;
          font-size: 32px;
          text-align: center;
          letter-spacing: 8px;
          border: 3px solid #e0e0e0;
          border-radius: 12px;
          font-weight: bold;
          text-transform: uppercase;
          font-family: monospace;
          margin-bottom: 24px;
        }

        .invite-code-input:focus {
          outline: none;
          border-color: #4285f4;
        }

        .modal-actions {
          display: flex;
          gap: 12px;
        }

        .btn-modal-primary {
          flex: 1;
          padding: 14px;
          background: #4285f4;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .btn-modal-primary:hover:not(:disabled) {
          background: #357ae8;
        }

        .btn-modal-primary:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .btn-modal-secondary {
          flex: 1;
          padding: 14px;
          background: white;
          color: #666;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-modal-secondary:hover {
          border-color: #4285f4;
          color: #4285f4;
        }

        .qr-hint {
          margin-top: 24px;
          padding: 16px;
          background: #e3f2fd;
          border-radius: 8px;
        }

        .qr-hint p {
          margin: 0;
          color: #1565c0;
          font-size: 14px;
        }

        .checkin-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .dashboard-error {
          margin-bottom: 24px;
          padding: 12px 16px;
          background: #ffebee;
          color: #c62828;
          border-radius: 8px;
        }

        .dashboard-problem {
          max-width: 600px;
          margin: 60px auto;
          padding: 0 16px;
          text-align: center;
        }

        .dashboard-problem h1 {
          font-size: 26px;
          margin-bottom: 12px;
        }

        .dashboard-problem p {
          color: #555;
          margin-bottom: 24px;
        }

        .dashboard-problem .header-actions {
          justify-content: center;
        }

        @media (max-width: 640px) {
          .dashboard {
            padding: 24px 16px;
          }

          .dashboard-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
          }

          .dashboard-header h1 {
            font-size: 24px;
            overflow-wrap: anywhere;
          }

          .header-actions {
            flex-wrap: wrap;
          }

          .dashboard-tabs {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .tab {
            padding: 10px 12px;
            font-size: 14px;
            white-space: nowrap;
          }

          .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .contacts-grid {
            grid-template-columns: 1fr;
          }

          .contact-card {
            padding: 16px;
          }

          .contact-info {
            min-width: 0;
            overflow-wrap: anywhere;
          }

          .request-header,
          .request-footer {
            flex-wrap: wrap;
            gap: 8px;
          }

          .profile-card {
            padding: 20px;
          }

          .profile-avatar-large {
            width: 88px;
            height: 88px;
            font-size: 36px;
          }

          .modal-content {
            padding: 24px 20px;
            width: calc(100% - 32px);
          }

          .invite-code-input {
            font-size: 24px;
            letter-spacing: 4px;
          }

          .modal-actions {
            flex-direction: column-reverse;
          }
        }
    `}</style>
  );
}
